import React, { ChangeEvent, KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./EcommerceQuickOrder.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import { useCart } from "@contexts/CartContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import { ICartItemToAdd } from "@models/DTO/IBulkAddItemsToCartDTO";
import IQuickOrderRowUI from "@models/IQuickOrderRowUI";
import IOrder from "@models/IOrder";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import CartService from "@services/CartService";
import EcommerceCardViewService from "@services/EcommerceCardViewService";
import HelperService from "@services/HelperService";
import OrderEntryService from "@services/OrderEntryService";
import SupplierService from "@services/SupplierService";
import UserService from "@services/UserService";
import EcommerceCardFieldEnum from "@models/EcommerceCardFieldEnum";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwP } from "../../shared/OwP/OwP";
import ICatalog_Item from "@models/ICatalog_Item";
import FavoritesService, { FAVORITES_MAX_PAGE_SIZE } from "@services/FavoritesService";
import { useFavorites } from "@contexts/FavoritesContext";
import { useFavoritesFromRecentModal } from "@hooks/ecommerce/useFavoritesFromRecentModal";
import { EcommerceFavoriteToggle } from "../EcommerceFavoriteToggle/EcommerceFavoriteToggle";

type Props = {
    toggleCartSidebar: () => void;
};

/** "All items" is the portal home fetch; "Favorites" is the customer's own list (OR-1488). */
export type QuickOrderTab = "all" | "favorites";

// Echoed back on the typeahead response so this view and the card/list views
// (which share the same socket event) each consume only their own results.
const QUICK_ORDER_STATE = "quickOrder";
const MAX_ROWS = 100;
// Per-line qty ceiling: keeps runaway typed values well inside safe-integer range
// so extended-price math and the bulk-add payload stay exact.
const MAX_QTY = 999999;

export const EcommerceQuickOrder: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();
    const { refreshCart } = useCart();
    const favorites = useFavorites();
    const { openFavoritesFromRecentModal } = useFavoritesFromRecentModal(uiContext);

    const [rows, setRows] = useState<IQuickOrderRowUI[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tab, setTab] = useState<QuickOrderTab>("all");
    // The latest favorites fetch failed: the empty branch shows an error, not the "no favorites" prompt.
    const [favoritesLoadError, setFavoritesLoadError] = useState(false);
    // Which tab's data the next response belongs to; stale/foreign responses are dropped.
    // Synced after commit (not during render, which React may discard) and before the
    // passive tab-change effect below reads it.
    const tabRef = useRef<QuickOrderTab>("all");
    useLayoutEffect(() => {
        tabRef.current = tab;
    }, [tab]);
    const favoritesRequestIdRef = useRef(0);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const footerRef = useRef<HTMLDivElement | null>(null);

    // The footer is position: fixed (the app's nested scroll containers defeat sticky),
    // so it must be aligned to the grid container's box — otherwise a viewport-wide bar
    // runs under the side menus. The sidebars animate open/closed, so a one-shot
    // measurement goes stale: instead, each animation frame nudges the footer by the
    // difference between where it is and where the container is. Styles are written
    // directly (not via state) to avoid a re-render per frame.
    useEffect(() => {
        const container = containerRef.current;
        const footer = footerRef.current;

        if (!container || !footer) {
            return;
        }

        let frameId: number;

        const track = () => {
            const containerRect = container.getBoundingClientRect();
            const footerRect = footer.getBoundingClientRect();

            const leftDelta = containerRect.left - footerRect.left;
            const widthDelta = containerRect.width - footerRect.width;

            if (Math.abs(leftDelta) >= 0.5 || Math.abs(widthDelta) >= 0.5) {
                footer.style.left = `${(parseFloat(footer.style.left) || 0) + leftDelta}px`;
                footer.style.width = `${(parseFloat(footer.style.width) || footerRect.width) + widthDelta}px`;
            }

            frameId = requestAnimationFrame(track);
        };

        track();

        return () => cancelAnimationFrame(frameId);
    }, [isLoading, rows.length]);

    const supplierVendorToken = UserService.getSupplierVendorToken(vendorModel, supplierModel.id);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    // Customer par column: par-level suppliers only, and only while the card view config shows
    // the par field — one switch drives the card, list and quick-order views.
    const showPar =
        !!supplierModel.has_par_levels &&
        EcommerceCardViewService.normalizeCardView(SupplierService.getSupplierSettings(supplierModel)?.screens?.eCommerce?.cardView)
            .visibility[EcommerceCardFieldEnum.PAR];

    // Suppliers may allow fractional quantities (qty_precision 1-3, matching the
    // card/list views); 0 or unset keeps the original integer-only entry.
    const qtyPrecision = supplierModel.qty_precision || 0;

    const sanitizeQtyInput = (raw: string): string => {
        if (qtyPrecision <= 0) {
            // Integer-only: ignore anything typed after a decimal point rather than
            // collapsing "1.5" into 15.
            return raw
                .split(".")[0]
                .replace(/\D/g, "")
                .replace(/^0+(?=\d)/, "");
        }

        // Keep digits and the first decimal point; excess decimals are truncated to
        // the supplier's precision rather than rounded so typing never jumps.
        const [whole, ...decimals] = raw.replace(/[^\d.]/g, "").split(".");
        const cleanWhole = whole.replace(/^0+(?=\d)/, "");

        if (!decimals.length) {
            return cleanWhole;
        }

        return `${cleanWhole}.${decimals.join("").slice(0, qtyPrecision)}`;
    };

    // In-progress quantities autosave to localStorage so an abandoned tab / dead session
    // doesn't lose a half-keyed order. Cleared once the lines land in the cart.
    const draftStorageKey = `ow_quick_order_draft:${supplierModel.guid}:${vendorModel.guid}:${userModel.guid}`;

    const readDraftQtys = (): Record<string, number> => {
        try {
            const raw = localStorage.getItem(draftStorageKey);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    // Shared by both tabs: catalog items -> grid rows, restoring any autosaved draft quantities.
    const buildRows = (catalogItems: ICatalog_Item[]): IQuickOrderRowUI[] => {
        const draftQtys = readDraftQtys();

        // Same pricing inputs the card/list views use (EcommerceListView).
        const pricingOrder = {
            Price_Group: vendorModel.Price_Group,
            supplierVendorToken,
        } as IOrder;

        return (
            catalogItems
                // Product groups and advanced kits need option/builder modals the grid can't host.
                .filter((item) => !item.is_product_group && !(item.is_kit && item.is_advanced_kit))
                .slice(0, MAX_ROWS)
                .map((item) => {
                    const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(item, supplierModel, pricingOrder);
                    const draftQty = draftQtys[item.guid] || 0;

                    return {
                        guid: item.guid,
                        catalogItem: item,
                        sku: item.sku || "",
                        name: item.name,
                        unitName: item.Unit_Of_Measurement?.name?.toLowerCase() || "",
                        customerParQty: item.customer_par_qty ?? null,
                        unitPrice: priceDetail.discountedPrice,
                        discountId: priceDetail.discount?.id,
                        qty: draftQty,
                        qtyText: draftQty ? String(draftQty) : "",
                    };
                })
        );
    };

    const onGetTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        if (data.state !== QUICK_ORDER_STATE || tabRef.current !== "all") {
            return;
        }

        setRows(buildRows(data.catalog_items));
        setIsLoading(false);
    };

    // Favorites tab: the customer's list in the card shape (newest first, availability-filtered
    // server-side) over REST — no socket round-trip to demultiplex.
    const loadFavoritesRows = async () => {
        const requestId = ++favoritesRequestIdRef.current;

        setIsLoading(true);

        try {
            const result = await FavoritesService.getFavorites(
                { limit: FAVORITES_MAX_PAGE_SIZE, offset: 0 },
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );

            if (requestId !== favoritesRequestIdRef.current || tabRef.current !== "favorites") {
                return;
            }

            setFavoritesLoadError(false);
            setRows(buildRows(result.catalog_items || []));
        } catch (error) {
            if (requestId === favoritesRequestIdRef.current) {
                setFavoritesLoadError(true);
                setRows([]);
                uiContext.notifyFn({
                    eid: "",
                    title: "error",
                    message: error instanceof Error ? error.message : "favorites_load_failed",
                });
            }
        } finally {
            if (requestId === favoritesRequestIdRef.current) {
                setIsLoading(false);
            }
        }
    };

    const getQuickOrderItems = () => {
        if (!vendorModel.Catalog) {
            setIsLoading(false);
            return;
        }

        // Same fetch that populates the portal home view: common items, falling back to
        // standard items — and template items when the supplier has template_home_items
        // enabled and the customer has a template (handled server-side).
        const payload: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: vendorModel.Catalog.guid,
            name: "",
            limit: MAX_ROWS,
            offset: 0,
            onlyActive: true,
            includeKitItems: true,
            priceGroupGuid: vendorModel.Price_Group?.guid,
            onlyCommonItems: true,
            includeStandardIfNoCommonItems: true,
            state: QUICK_ORDER_STATE,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        setIsLoading(true);
        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onGetTypeaheadCatalogItem);

        return () => {
            socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onGetTypeaheadCatalogItem);
        };
    }, []);

    // Load the active tab's items on mount and on every tab switch. Favorites are fetched on tab
    // entry only (not on every toggle) so a row a customer just unfavorited mid-entry isn't yanked
    // away with its typed quantity.
    useEffect(() => {
        if (tab === "favorites") {
            loadFavoritesRows();
        } else {
            getQuickOrderItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // Persist the draft on every change (covers typing, submit clearing, and partial
    // failures — whatever remains keyed in is what's saved). An empty rows array means
    // nothing loaded, so leave any existing draft alone. Drafts are keyed by item guid across
    // both tabs, so merge with what's stored: only the rows on screen are updated or cleared.
    useEffect(() => {
        if (isLoading || !rows.length) {
            return;
        }

        const draftQtys: Record<string, number> = readDraftQtys();

        for (const row of rows) {
            if (row.qty > 0) {
                draftQtys[row.guid] = row.qty;
            } else {
                delete draftQtys[row.guid];
            }
        }

        try {
            if (Object.keys(draftQtys).length) {
                localStorage.setItem(draftStorageKey, JSON.stringify(draftQtys));
            } else {
                localStorage.removeItem(draftStorageKey);
            }
        } catch {
            // Storage unavailable (private mode / quota) — autosave is best-effort.
        }
    }, [rows, isLoading]);

    const handleQtyChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        let qtyText = sanitizeQtyInput(e.target.value);
        // A lone "." parses to NaN — treat in-progress entry as 0 until digits land.
        let qty = qtyText === "" ? 0 : Number(qtyText) || 0;

        if (qty > MAX_QTY) {
            qty = MAX_QTY;
            qtyText = String(MAX_QTY);
        }

        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, qty, qtyText, error: undefined } : row)));
    };

    const handleQtyKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case "ArrowDown":
            case "Enter":
                e.preventDefault();
                inputRefs.current[index + 1]?.focus();
                break;
            case "ArrowUp":
                e.preventDefault();
                inputRefs.current[index - 1]?.focus();
                break;
            default:
                break;
        }
    };

    const formatPrice = (value: number) => {
        return HelperService.formatCurrency({
            value,
            precision: supplierModel.pricing_precision || 2,
            currency: "USD",
        });
    };

    const totalUnits = rows.reduce((sum, row) => sum + row.qty, 0);
    const lineCount = rows.filter((row) => row.qty > 0).length;
    const orderTotal = rows.reduce((sum, row) => sum + row.qty * row.unitPrice, 0);

    const submitQuickOrder = async () => {
        const linesToAdd = rows.filter((row) => row.qty > 0);

        if (!linesToAdd.length || isSubmitting) {
            return;
        }

        const items: ICartItemToAdd[] = linesToAdd.map((row) => {
            const item: ICartItemToAdd = {
                catalogItemGuid: row.guid,
                qty: row.qty,
            };

            if (supplierModel.has_discounts && row.discountId) {
                item.discountId = row.discountId;
            }

            return item;
        });

        setIsSubmitting(true);
        uiContext.setActivityIndicator(true);

        try {
            const response = await CartService.bulkAddItemsToCart(
                items,
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );

            await refreshCart();

            const failuresByGuid = new Map(
                response.results.filter((result) => !result.success).map((result) => [result.catalogItemGuid, result])
            );

            if (failuresByGuid.size === 0) {
                // Full success: clear the grid for the next round of entry.
                setRows((prev) => prev.map((row) => ({ ...row, qty: 0, qtyText: "", error: undefined })));

                uiContext.notifyFn(null, {
                    message: "quick_order_items_added",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                } as INotification);

                // Match the single-item add-to-cart desktop behavior (Bootstrap md breakpoint).
                if (window.innerWidth >= 768) {
                    props.toggleCartSidebar();
                }
            } else {
                // Clear only the lines that made it into the cart; keep failed lines keyed
                // in with their error so the user can adjust and resubmit.
                setRows((prev) =>
                    prev.map((row) => {
                        if (row.qty <= 0) {
                            return row;
                        }

                        const failure = failuresByGuid.get(row.guid);

                        if (failure) {
                            return { ...row, error: failure.message || "" };
                        }

                        return { ...row, qty: 0, qtyText: "", error: undefined };
                    })
                );

                uiContext.notifyFn(null, {
                    message: failuresByGuid.size < items.length ? "quick_order_partial_success" : "quick_order_failed",
                    level: NotificationLevelEnum.warning,
                    position: NotificationPositionEnum.bc,
                } as INotification);
            }
        } catch (error) {
            uiContext.notifyFn({
                eid: "",
                title: "error",
                message: error instanceof Error ? error.message : "quick_order_failed",
            });
        } finally {
            setIsSubmitting(false);
            uiContext.setActivityIndicator(false);
        }
    };

    const isFavoritesTab = tab === "favorites";

    const buildFavoritesFromRecent = () => {
        openFavoritesFromRecentModal({
            onAdded: () => {
                if (tabRef.current === "favorites") {
                    loadFavoritesRows();
                }
            },
            onViewFullCatalog: () => setTab("all"),
        });
    };

    const renderHeader = () => (
        <div className={"quick-order-header d-flex justify-content-between align-items-center flex-wrap gap-2"}>
            <div className={"d-flex align-items-center flex-wrap gap-3"}>
                <OwLabel title={"quick_order"} isBold={true} noBottomMargin={true} />
                {favorites.enabled ? (
                    <div className={"quick-order-tabs btn-group btn-group-sm"} role={"tablist"} aria-label={"Quick order items"}>
                        <OwButton
                            className={"btn-primary " + (!isFavoritesTab ? "active" : "")}
                            title={"all_items"}
                            ariaLabel={"All items"}
                            fn={() => setTab("all")}
                            testId={"quick-order-tab-all"}
                        />
                        <OwButton
                            className={"btn-primary " + (isFavoritesTab ? "active" : "")}
                            title={"favorites"}
                            leftIconClass={"fas fa-heart"}
                            ariaLabel={"Favorites"}
                            fn={() => setTab("favorites")}
                            testId={"quick-order-tab-favorites"}
                        />
                    </div>
                ) : null}
            </div>
            <div className={"quick-order-hint text-muted"}>
                <i className={"fad fa-arrow-up me-1"} />
                <i className={"fad fa-arrow-down me-1"} />
                <OwLabel title={"quick_order_keyboard_hint"} noBottomMargin={true} />
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div className={"EcommerceQuickOrder"} ref={containerRef}>
                {renderHeader()}
                <div className={"d-flex justify-content-center align-items-center py-5"}>
                    <ActivityIndicator active={true} className={"me-2"} />
                    <OwLabel title={"loading"} noBottomMargin={true} />
                </div>
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className={"EcommerceQuickOrder"} ref={containerRef}>
                {renderHeader()}
                {isFavoritesTab && favoritesLoadError ? (
                    <div className={"quick-order-favorites-empty text-center py-5"} data-testid={"quick-order-favorites-error"}>
                        <OwP content={"favorites_load_failed"} className={"text-muted"} />
                        <OwButton className={"btn-outline-secondary"} title={"view_full_catalog"} fn={() => setTab("all")} />
                    </div>
                ) : isFavoritesTab && favorites.enabled ? (
                    <div className={"quick-order-favorites-empty text-center py-5"} data-testid={"quick-order-favorites-empty"}>
                        <i className={"far fa-heart quick-order-favorites-empty-icon"} aria-hidden={true} />
                        <OwLabel title={"favorites_empty_title"} isBold={true} className={"d-block mb-1"} />
                        <OwP content={"favorites_empty_prompt"} className={"text-muted"} />
                        <div className={"d-flex flex-wrap justify-content-center gap-2 mt-3"}>
                            <OwButton
                                className={"btn-primary"}
                                title={"build_from_recent_orders"}
                                leftIconClass={"fas fa-history"}
                                fn={buildFavoritesFromRecent}
                            />
                            <OwButton className={"btn-outline-secondary"} title={"view_full_catalog"} fn={() => setTab("all")} />
                        </div>
                    </div>
                ) : (
                    <div className={"text-center py-5"}>
                        <OwP content={"quick_order_no_items"} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={"EcommerceQuickOrder"} ref={containerRef}>
            {renderHeader()}
            <table className={"quick-order-table table align-middle"}>
                <thead>
                    <tr>
                        {/* On phones the SKU folds into the Product cell and Unit/Ext. Price hide —
                            three columns (Product, Price, Qty) fit the viewport; the footer total
                            still updates live in place of the per-row extended price. */}
                        <th className={"d-none d-md-table-cell"}>
                            <OwLabel title={"sku"} noBottomMargin={true} />
                        </th>
                        <th>
                            <OwLabel title={"product"} noBottomMargin={true} />
                        </th>
                        <th className={"d-none d-md-table-cell"}>
                            <OwLabel title={"unit"} noBottomMargin={true} />
                        </th>
                        {showPar ? (
                            <th className={"d-none d-md-table-cell text-center"}>
                                <OwLabel title={"par_level"} noBottomMargin={true} />
                            </th>
                        ) : null}
                        {!hidePricing ? (
                            <th className={"text-end"}>
                                <OwLabel title={"price"} noBottomMargin={true} />
                            </th>
                        ) : null}
                        <th className={"text-center"}>
                            <OwLabel title={"qty"} noBottomMargin={true} />
                        </th>
                        {!hidePricing ? (
                            <th className={"d-none d-md-table-cell text-end"}>
                                <OwLabel title={"extended_price"} noBottomMargin={true} />
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={row.guid}>
                            <td className={"d-none d-md-table-cell text-small"}>{row.sku}</td>
                            <td>
                                <div className={"d-flex align-items-start gap-1"}>
                                    {/* Inline in the product cell rather than its own column so the phone layout stays three columns wide. */}
                                    <EcommerceFavoriteToggle
                                        catalogItemGuid={row.guid}
                                        inline={true}
                                        size={"sm"}
                                        className={"flex-shrink-0"}
                                    />
                                    <div className={"flex-grow-1"} style={{ minWidth: 0 }}>
                                        {row.name}
                                        <div className={"d-md-none text-small text-muted"}>{row.sku}</div>
                                        {row.error ? <div className={"text-danger text-small"}>{row.error}</div> : null}
                                    </div>
                                </div>
                            </td>
                            <td className={"d-none d-md-table-cell"}>{row.unitName}</td>
                            {showPar ? <td className={"d-none d-md-table-cell text-center"}>{row.customerParQty ?? "—"}</td> : null}
                            {!hidePricing ? <td className={"text-end"}>{formatPrice(row.unitPrice)}</td> : null}
                            <td className={"text-center"}>
                                <input
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    className={"quick-order-qty form-control form-control-sm " + (row.error ? "is-invalid" : "")}
                                    type={"text"}
                                    inputMode={qtyPrecision > 0 ? "decimal" : "numeric"}
                                    pattern={qtyPrecision > 0 ? undefined : "[0-9]*"}
                                    autoComplete={"off"}
                                    aria-label={row.name}
                                    value={row.qtyText}
                                    onChange={(e) => handleQtyChange(index, e)}
                                    onKeyDown={(e) => handleQtyKeyDown(index, e)}
                                    onFocus={(e) => e.target.select()}
                                />
                            </td>
                            {!hidePricing ? (
                                <td className={"d-none d-md-table-cell text-end"}>
                                    {row.qty > 0 ? formatPrice(row.qty * row.unitPrice) : "—"}
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={"quick-order-footer"} ref={footerRef}>
                <div>
                    <OwLabel title={"total_units"} noBottomMargin={true} isBold={true} />
                    <div>{totalUnits}</div>
                </div>
                <div>
                    <OwLabel title={"lines"} noBottomMargin={true} isBold={true} />
                    <div>{lineCount}</div>
                </div>
                {!hidePricing ? (
                    <div>
                        <OwLabel title={"total"} noBottomMargin={true} isBold={true} />
                        <div>{formatPrice(orderTotal)}</div>
                    </div>
                ) : null}
                <OwButton
                    className={"btn-primary"}
                    title={"add_all_to_cart"}
                    disabled={lineCount === 0 || isSubmitting}
                    fn={submitQuickOrder}
                />
            </div>
        </div>
    );
};

export default EcommerceQuickOrder;
